// The email Baker sends a loan officer: their Mortgage month, in plain words.
//
// Pure and import-light so the preview on screen and the message that is
// actually sent come from the SAME function and cannot drift. Built on the
// server at send time from fresh data; the browser never supplies the body.
//
// Voice: short, plain, no dashes used as punctuation.

import { monthYearLabel } from "./disbursement-label";
import { buildPaycheckLabels, splitPaychecks } from "./split-paychecks";
import type { MortgageMonthlySummary, MortgageSummaryEmail } from "./mortgage-monthly-summary-types";

export const DEFAULT_SUMMARY_NOTE =
  "Please reply with anything that looks wrong or missing.";

function money(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const dollars = Math.floor(abs / 100).toLocaleString("en-US");
  return `${sign}$${dollars}.${String(abs % 100).padStart(2, "0")}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function firstName(full: string): string {
  return full.trim().split(/\s+/)[0] || full;
}

/** A group's header, saying plainly when the list shown is not the whole group. */
function groupHeader(title: string, shown: number, total: number): string {
  return shown < total ? `${title} (${total}, showing the first ${shown})` : `${title} (${total})`;
}

/**
 * The two gross paychecks for the approved part of the month, in the same
 * words and the same rounding payroll uses (split-paychecks.ts is the one
 * source). Empty when nothing is approved yet: two $0 lines would read as
 * a real instruction to payroll.
 */
function paycheckLines(summary: MortgageMonthlySummary): string[] {
  const { decided_cents: decided, undecided_count: undecided } = summary.commissioned;
  const lines: string[] = [];
  if (decided > 0) {
    const checks = splitPaychecks(decided, 2).paychecks_cents;
    const labels = buildPaycheckLabels(summary.period);
    checks.forEach((cents, i) => lines.push(`${labels[i] ?? `Paycheck ${i + 1}`}: ${money(cents)}`));
    lines.push("Gross per check, before payroll deductions.");
  }
  if (undecided > 0) {
    const noun = undecided === 1 ? "1 commission is" : `${undecided} commissions are`;
    lines.push(
      decided > 0
        ? `${noun} not approved yet, so these checks can still change.`
        : `${noun} not approved yet, so there is no paycheck split to show.`,
    );
  }
  return lines;
}

export function buildMortgageSummaryEmail(
  summary: MortgageMonthlySummary,
  opts: { note: string | null; senderName: string },
): MortgageSummaryEmail {
  const month = monthYearLabel(summary.period);
  const note = (opts.note ?? "").trim() || DEFAULT_SUMMARY_NOTE;
  const { commissioned, awaiting_worksheet: awaiting, still_open: open } = summary;

  const subject = `Mortgage summary for ${month}`;

  const textLines: string[] = [
    `Hi ${firstName(summary.advisor.name)},`,
    "",
    `Here is where your Mortgage files stand for ${month}.`,
    note,
    "",
    groupHeader("Closed and commissioned", commissioned.rows.length, commissioned.total),
  ];
  if (commissioned.total === 0) textLines.push("  None this month.");
  for (const r of commissioned.rows) {
    textLines.push(`  ${r.label}: gross ${money(r.gross_cents)}, your commission ${money(r.advisor_cents)} (${r.status})`);
  }
  if (commissioned.total > 0) {
    textLines.push(`  Total: gross ${money(commissioned.gross_cents)}, your commission ${money(commissioned.advisor_cents)}`);
  }
  const checks = commissioned.total > 0 ? paycheckLines(summary) : [];
  if (checks.length > 0) textLines.push("", "Paycheck split", ...checks.map((l) => `  ${l}`));
  textLines.push("", groupHeader("Closed, no commission worksheet yet", awaiting.rows.length, awaiting.total));
  if (awaiting.total === 0) textLines.push("  None.");
  for (const r of awaiting.rows) textLines.push(`  ${r.label}${r.closed_on ? ` (closed ${r.closed_on})` : ""}`);
  textLines.push("", groupHeader("Still open", open.rows.length, open.total));
  if (open.total === 0) textLines.push("  None.");
  for (const r of open.rows) {
    textLines.push(`  ${r.label}: ${r.stage}${r.expected_close ? `, expected close ${r.expected_close}` : ""}`);
  }
  textLines.push("", "Thanks,", opts.senderName, "Stewardship", "");
  const text = textLines.join("\n");

  const li = (s: string) => `<li>${escapeHtml(s)}</li>`;
  const list = (items: string[], none: string) =>
    items.length === 0 ? `<p>${none}</p>` : `<ul>${items.map(li).join("")}</ul>`;
  const html =
    `<p>Hi ${escapeHtml(firstName(summary.advisor.name))},</p>` +
    `<p>Here is where your Mortgage files stand for ${escapeHtml(month)}.<br/>${escapeHtml(note).replace(/\n/g, "<br/>")}</p>` +
    `<p><strong>${escapeHtml(groupHeader("Closed and commissioned", commissioned.rows.length, commissioned.total))}</strong></p>` +
    list(
      commissioned.rows.map(
        (r) => `${r.label}: gross ${money(r.gross_cents)}, your commission ${money(r.advisor_cents)} (${r.status})`,
      ),
      "None this month.",
    ) +
    (commissioned.total > 0
      ? `<p>Total: gross ${money(commissioned.gross_cents)}, your commission ${money(commissioned.advisor_cents)}</p>`
      : "") +
    (checks.length > 0
      ? `<p><strong>Paycheck split</strong></p><p>${checks.map(escapeHtml).join("<br/>")}</p>`
      : "") +
    `<p><strong>${escapeHtml(groupHeader("Closed, no commission worksheet yet", awaiting.rows.length, awaiting.total))}</strong></p>` +
    list(awaiting.rows.map((r) => `${r.label}${r.closed_on ? ` (closed ${r.closed_on})` : ""}`), "None.") +
    `<p><strong>${escapeHtml(groupHeader("Still open", open.rows.length, open.total))}</strong></p>` +
    list(
      open.rows.map((r) => `${r.label}: ${r.stage}${r.expected_close ? `, expected close ${r.expected_close}` : ""}`),
      "None.",
    ) +
    `<p>Thanks,<br/>${escapeHtml(opts.senderName)}<br/>Stewardship</p>`;

  return { to: summary.advisor.email, subject, text, html };
}
